<template>
  <component v-if="isLocal" :is="defaultFileItem" :item="item" :question="question" />
  <span v-else :class="question.cssClasses.previewItem">
    <button
      type="button"
      class="btn btn-link text-break"
      :disabled="downloading"
      @click.stop="download"
    >
      {{ downloading ? 'Preparing download…' : (item.name || 'Download attachment') }}
    </button>

    <PrivateFileImage
      v-if="isImage"
      :load-access="loadAccess"
      :alt="item.name || 'Attachment preview'"
      error-message="Could not load the attachment preview."
    />
    <div v-if="error" class="text-danger small" role="alert">{{ error }}</div>
  </span>
</template>

<script>
import { ComponentFactory } from 'survey-vue3-ui';
import { watchApiSession } from '@/api';
import { ATTACHMENT_CRYPTO_ERROR_MESSAGE, licenseAttachmentAccess } from '@/privateFiles';
import { getSurveyLicenseContext } from '@/surveyLicenseContext';
import PrivateFileImage from '@/components/common/PrivateFileImage.vue';

// Preserve SurveyJS's native controls for newly selected files in editable/public forms.
const defaultFileItem = ComponentFactory.Instance.getComponent('sv-file-item');

export default {
  components: { PrivateFileImage },
  props: {
    item: {
      type: Object,
      required: true
    },
    question: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      downloading: false,
      error: null,
      controller: null,
      stopSession: null,
      disposed: false,
      requestVersion: 0
    };
  },
  computed: {
    defaultFileItem() {
      return defaultFileItem;
    },
    isLocal() {
      return /^(data:|blob:)/.test(this.item.content);
    },
    isImage() {
      return ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(this.item.type?.toLowerCase());
    },
    context() {
      return {
        ...getSurveyLicenseContext(this.question),
        content: this.item.content
      };
    },
    /** Capture the current saved reference so changing records replaces the image request. */
    loadAccess() {
      const context = this.context;

      return options => licenseAttachmentAccess(context, options);
    }
  },
  watch: {
    context() {
      this.cancel();
    }
  },
  async mounted() {
    if (this.isLocal) {
      return;
    }

    try {
      const stop = await watchApiSession(() => this.cancel());

      if (this.disposed) {
        stop();
      } else {
        this.stopSession = stop;
      }
    } catch {
      this.error = 'Could not prepare this attachment. Please try again.';
    }
  },
  beforeUnmount() {
    this.disposed = true;
    this.stopSession?.();
    this.cancel();
  },
  methods: {
    /** Invalidate pending access requests when the component, file or account changes. */
    cancel() {
      this.requestVersion += 1;
      this.controller?.abort();
      this.downloading = false;
      this.error = null;
    },

    /** Authorize each click and let Storage deliver an attachment directly, without a popup or Blob. */
    async download() {
      if (this.downloading || this.disposed) {
        return;
      }

      this.cancel();
      const version = this.requestVersion;
      this.controller = new AbortController();
      this.downloading = true;

      try {
        const access = await licenseAttachmentAccess(this.context, {
          download: true,
          signal: this.controller.signal
        });

        if (version !== this.requestVersion) {
          return;
        }

        // The signed response forces attachment disposition, so this preserves the current page.
        const link = document.createElement('a');
        link.href = access.url;
        link.download = this.item.name || 'attachment';
        link.rel = 'noreferrer';
        link.referrerPolicy = 'no-referrer';

        // A detached link keeps this credential out of document-level click/analytics listeners.
        link.click();
      } catch (error) {
        if (version === this.requestVersion) {
          this.error = error?.code === 'attachment-crypto-unavailable'
            ? ATTACHMENT_CRYPTO_ERROR_MESSAGE
            : 'Could not download this attachment. Please try again.';
        }
      } finally {
        if (version === this.requestVersion) {
          this.downloading = false;
        }
      }
    }
  }
};
</script>
